/**
 * Unified Auth Utility — Single source of truth for TreatX FE auth
 * (RBAC-A1 / RBAC-FE convergence — canonical roles only)
 *
 * Keys used:
 *   localStorage.treatx_unified_token  — JWT from backend unified login
 *   localStorage.treatx_unified_user   — User payload { sub, email, role, clinic_id, ... }
 *
 * RBAC contract:
 *   - The only acceptable values for `user.role` are members of
 *     `CANONICAL_ROLES` (see `./canonicalRoles.js`).
 *   - Any stored token whose user-payload role is NOT canonical is
 *     considered a stale legacy token; this utility clears it.
 *   - The backend rejects legacy-role JWTs at the middleware with
 *     `401 legacy_role_in_token`; `handleAuthResponse` clears the
 *     session on any such failure and redirects to login.
 */

import {
  isCanonicalRole,
  isTreatxAdminRole,
  TREATX_ADMIN_ROLES,
} from './canonicalRoles';
import StorageService from '../services/StorageService';

const TOKEN_KEY = 'treatx_unified_token';
const USER_KEY = 'treatx_unified_user';

/**
 * Get the current unified auth state.
 * FIND-H-08 post-cutover: the auth token lives in the HttpOnly
 * `treatx_auth` cookie — JS cannot read it. `token` is deprecated and
 * always returned as `null` here (retained for legacy call sites that
 * only inspect `user`). The `user` snapshot lives in the readable
 * `treatx_unified_user` localStorage entry.
 * If the stored user-role is non-canonical, the session is cleared
 * and `{ token: null, user: null }` is returned.
 */
export function getUnifiedAuth() {
  let user = null;
  try {
    const raw = localStorage.getItem(USER_KEY);
    if (raw) user = JSON.parse(raw);
  } catch {
    clearUnifiedAuth();
    return { token: null, user: null };
  }

  // RBAC-FE: stale legacy-role → clear + force re-login.
  if (user && user.role && !isCanonicalRole(user.role)) {
    clearUnifiedAuth();
    return { token: null, user: null };
  }
  return { token: null, user };
}

/**
 * Is the current user a TreatX-side platform admin
 * (treatx_super_admin / treatx_admin)?
 */
export function isTreatxAdmin() {
  const { user } = getUnifiedAuth();
  return !!user && isTreatxAdminRole(user.role);
}

/**
 * Get auth headers for API calls. FIND-H-08 post-cutover: browser
 * TreatX auth is the HttpOnly `treatx_auth` cookie — the browser
 * attaches it automatically on `credentials: 'include'` requests.
 * Legacy callers still use this helper; we retain it as a Content-Type
 * shim only. NO Authorization header is emitted from browser code.
 */
export function authHeaders() {
  return { 'Content-Type': 'application/json' };
}

/**
 * Clear all unified auth keys on logout / stale-token detection.
 *
 * FIND-H-08: this helper only clears CLIENT state — it does NOT fire
 * the backend logout endpoint. Callers that need to expire the
 * server-side cookie (user-initiated logout, stale-token paths) must
 * invoke `POST /api/auth/logout` themselves. `AuthContext.logout()`
 * (canonical header-button surface) and `maybeClearStaleToken(status)`
 * both do so. Splitting server-side expiry from client-side state
 * clearing avoids double-firing the endpoint from the header logout.
 */
export function clearUnifiedAuth() {
  // Canonical keys — user snapshot only (token no longer stored).
  localStorage.removeItem(TOKEN_KEY);   // legacy purge — no-op post-cutover
  localStorage.removeItem(USER_KEY);
  localStorage.removeItem('treatx_rbac_permissions');
  localStorage.removeItem('treatx_rbac_role_assignments');
  // OF-001B — workspace catalogue + last active workspace
  localStorage.removeItem('treatx_rbac_workspaces');
  localStorage.removeItem('treatx_last_active_workspace');
  localStorage.removeItem('treatx_workspace_selection_provenance');
  sessionStorage.removeItem('treatx_active_clinic_id');
  sessionStorage.removeItem('treatx_clinic_user');
  // SECURITY-S4: Legacy mirrors that are no longer written but may
  // still exist in user browsers from prior sessions.  Sweep them all.
  localStorage.removeItem('treatx_user');
  localStorage.removeItem('treatx_pending_redirect');
  localStorage.removeItem('treatx_admin');
  localStorage.removeItem('treatx_coordinator_token');
  localStorage.removeItem('treatx_coordinator_user');
  localStorage.removeItem('clinicRepresentative');
  // O84-C — identity-scoped CRM caches (patients, leads, offers, clinic
  // profile) must not survive into the next user's session in this tab.
  StorageService.clearIdentityScopedCaches();
  // OF-001C — broadcast identity transition so identity-bound surfaces
  // (PendingInvitationsBanner, ClinicDataContext, RBACContext) reset
  // their in-memory React state.  Without this, prior-user invites
  // and workspace context leak across the logout boundary.
  try {
    // UAT-LOGIN-CLEAN-START-01 — mark that THIS navigation to /login is the
    // result of an explicit sign-out, so the login form can start visibly
    // clean. This is a one-shot UI hint in sessionStorage; it stores no
    // identity, and it never touches browser-saved credentials.
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.setItem('treatx_signed_out', '1');
    }
  } catch { /* storage disabled — the form simply behaves normally */ }
  try {
    if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
      window.dispatchEvent(new Event('treatx:identity-changed'));
    }
  } catch { /* SSR / test ctx — no-op */ }
}

/**
 * RBAC-FE token-staleness handler.
 *
 * Call this from any FE fetch wrapper after a network response.  If the
 * backend hard-fails the JWT (RBAC-A1 reasons `legacy_role_in_token` /
 * `non_canonical_assignment`, surfaced as a 401), this clears the
 * stored session + bounces the browser to `/login`.
 *
 * TREATX-P0-PROVIDER-APPLICATIONS-503-01:
 * Also handles the Soft Launch Gate 503 (`code: SOFT_LAUNCH_GATE_ACTIVE`).
 * The ASGI gate at `backend/middleware/soft_launch_gate_middleware.py`
 * intercepts every anonymous API call while the gate is enabled. When the
 * caller's stored token has been cleared / expired / never set, the
 * fetch goes out without an `Authorization: Bearer …` header and the
 * gate returns 503 instead of letting the request reach the route's
 * 401/403 RBAC guard. Treat that 503 the same way we treat a stale
 * 401 — clear any local session and redirect to `/login` — so admin
 * pages like AdminProviderDomain stop showing "Failed to load
 * applications (HTTP 503)" when the real problem is "you are logged
 * out".
 *
 * Usage:
 *   const res = await fetch(url, { headers: authHeaders() });
 *   if (maybeClearStaleToken(res)) return;
 *
 * Returns `true` when the response indicated a stale/legacy token or a
 * soft-launch-gate block and the session was cleared (so the caller
 * knows to abort).
 */
export function maybeClearStaleToken(response) {
  if (!response) return false;

  const status = response.status;
  const isStaleAuth = status === 401;
  const isSoftLaunchGate = status === 503;

  if (!isStaleAuth && !isSoftLaunchGate) return false;

  // For 503 we must positively identify the soft-launch gate — generic
  // 503s (genuine service outages) must surface to the caller unchanged.
  // The gate response carries `code: 'SOFT_LAUNCH_GATE_ACTIVE'`.
  // We do a fire-and-forget read of the cloned body to avoid consuming
  // the caller's response stream.
  if (isSoftLaunchGate) {
    const _redirectIfGate = async () => {
      try {
        const clone = response.clone();
        const body = await clone.json();
        if (body && body.code === 'SOFT_LAUNCH_GATE_ACTIVE') {
          clearUnifiedAuth();
          if (typeof window !== 'undefined' && window.location) {
            window.location.replace('/login');
          }
        }
      } catch {
        // Body not JSON or already consumed — leave the caller to handle.
      }
    };
    _redirectIfGate();
    // We optimistically return true: a soft-launch-gate 503 means the
    // caller must abort its happy path regardless of whether the body
    // clone resolves before the synchronous return.
    return true;
  }

  // status === 401 path (pre-existing behaviour)
  // FIND-H-08: token now HttpOnly-cookie-only; use the readable user
  // mirror as the "was authenticated" signal.
  let hadSession = false;
  try {
    hadSession = !!localStorage.getItem(USER_KEY);
    if (!hadSession && typeof document !== 'undefined') {
      hadSession = (document.cookie || '').includes('treatx_csrf=');
    }
  } catch { /* noop */ }
  // Only clear if we actually had a session to begin with — avoids
  // wiping anonymous flows that legitimately get 401s.
  if (!hadSession) return false;
  clearUnifiedAuth();
  // FIND-H-08 — best-effort server-side cookie expiry.
  try {
    if (typeof fetch !== 'undefined') {
      fetch(`${process.env.REACT_APP_BACKEND_URL || ''}/api/auth/logout`, {
        method: 'POST', credentials: 'include',
      }).catch(() => { /* noop */ });
    }
  } catch { /* noop */ }
  try {
    // Use replace so the back button doesn't re-trigger the loop.
    if (typeof window !== 'undefined' && window.location) {
      window.location.replace('/login');
    }
  } catch {
    // Non-browser context (SSR / tests) — no-op.
  }
  return true;
}

/**
 * Strict guard: assert that the current stored user role is canonical.
 * Used by route guards / page guards.  Returns the canonical role
 * string, or `null` if the session was stale and got cleared.
 */
export function getCanonicalRoleOrClear() {
  const { user } = getUnifiedAuth();
  if (!user || !user.role) return null;
  if (!isCanonicalRole(user.role)) {
    clearUnifiedAuth();
    return null;
  }
  return user.role;
}

// Re-exports for legacy import paths (no behaviour change).
export { TREATX_ADMIN_ROLES };
